import { Component, OnDestroy, OnInit } from "@angular/core";
import { Title } from "@angular/platform-browser";
import { ActivatedRoute } from "@angular/router";

import { TranslateService } from "@ngx-translate/core";

import { select, Store } from "@ngrx/store";
import { Observable, Subscription, zip } from "rxjs";
import { map } from "rxjs/operators";

import {
  getProductsExcelList,
  getProductsList,
  resetProductsState,
} from "src/app/store/actions/products/product.actions";
import {
  AppState,
  loadedMenusState,
  loadedProductsTableConfig,
} from "src/app/store/reducers";

import {
  FilterEvent,
  PageEvent,
  SearchEvent,
  SortEvent,
  DefaultPage,
  ExportEvent,
  TableEvent,
  ColumnSorterInfo,
} from "src/app/models/custom-table/custom-table.model";
import { MenuDTO, RouteData } from "src/app/models/Menu/MenuDTO";

import { subMenusIterator } from "src/app/shared/utils/menuUtils";
import { setColumnInfoCategoriesProperties } from "src/app/shared/utils/tableUtils";

import { BaseProductsList } from "src/app/shared/base-classes/base-products-list/base-products-list";

const PRODUCT_USAGE_CODE = "PRODUCT";

@Component({
  selector: "app-products-list",
  templateUrl: "./product-list.component.html",
  styleUrls: ["./product-list.component.scss"],
})
export class ProductListComponent 
  extends BaseProductsList 
  implements OnInit, OnDestroy 
{
  menus$: Observable<MenuDTO[]>;
  menuSub: Subscription;

  constructor(
    private titleService: Title,
    private route: ActivatedRoute,
    store: Store<AppState>,
    translate: TranslateService
  ) {
    super(store, translate);

    this.columnInfo$ = this.store.pipe(select(loadedProductsTableConfig)).pipe(
      map((columnInfo) => {
        if (columnInfo) {
          return setColumnInfoCategoriesProperties(columnInfo, this.tableConfig);
        }
      })
    );
  }

  ngOnInit(): void {   
    this.menus$ = this.store.pipe(select(loadedMenusState));

    this.loadScreenTitle();
    this.getProductCategoriesProperties(PRODUCT_USAGE_CODE, this.getProducts.bind(this));
  }

  loadScreenTitle() {
    this.menuSub = this.menus$.subscribe((menus: MenuDTO[]) => {
      if (menus && menus.length) {
        this.route.data.subscribe((routeData: RouteData) => {
          menus.forEach((menuEl: MenuDTO) => {
            const selectedMenu = subMenusIterator(menuEl, routeData);
            if (selectedMenu) {
              this.headerIcon = selectedMenu.icon;
              this.tableTitle = selectedMenu.description;
              this.titleService.setTitle(this.tableTitle);
            }
          });
        });
      }
    });
  }

  getProducts(
    pageEvent?: PageEvent,
    sortEvent?: SortEvent,
    searchEvent?: SearchEvent,
    filterEvent?: FilterEvent
  ) {
    const { pageIndex, pageSize } = pageEvent || {};
    this.store.dispatch(
      getProductsList({
        options: {
          paging: {
            skip: (pageIndex * pageSize) | DefaultPage.PAGE_INDEX,
            top: pageSize ?? DefaultPage.PAGE_SIZE,
            realSize: true,
          },
          filteringArgs: searchEvent,
          sortingArgs: sortEvent,
          advancedFilteringArgs: filterEvent,
        },
      })
    );
  }

  getProductsExcelList(
    pageEvent?: PageEvent,
    sortEvent?: SortEvent,
    searchEvent?: SearchEvent,
    filterEvent?: FilterEvent
  ) {
    const { pageIndex, pageSize } = pageEvent || {};
    this.store.dispatch(
      getProductsExcelList({
        options: {
          paging: {
            skip: (pageIndex * pageSize) | DefaultPage.PAGE_INDEX,
            top: pageSize ?? DefaultPage.EXCEL_PAGE_SIZE,
          },
          filteringArgs: searchEvent,
          sortingArgs: sortEvent,
          advancedFilteringArgs: filterEvent,
        },
      })
    );
  }

  onGetTableData(tableEvent: TableEvent) {
    this.getTableData(tableEvent, this.getProducts.bind(this));
  }

  onGetExportData(exportEvent: ExportEvent) {
    this.getExportData(exportEvent, this.getProductsExcelList.bind(this));
  }

  onSaveTableConfig(tableColumnInfo: ColumnSorterInfo[]) {
    this.saveTableConfig(tableColumnInfo, PRODUCT_USAGE_CODE);
  }

  ngOnDestroy() {
    if (this.productSubs) {
      this.productSubs.unsubscribe();
    }
    if (this.multiSubs) {
      this.multiSubs.unsubscribe();
    }
    if (this.menuSub) {
      this.menuSub.unsubscribe();
    }
    this.store.dispatch(resetProductsState());
  }
}
